/* comp-search-overlay.js — KOSHO SG full-screen predictive search.
 * ----------------------------------------------------------------------------
 * Spec: 機能要件定義書 §4.5 / Q1-19 (Shopify Predictive Search wiring)
 *
 * Responsibilities:
 *   - Open / close the overlay (fade, scroll-lock, focus-trap, ESC).
 *   - Subscribe to header `kosho-header:open-search` and direct clicks on
 *     [data-action="open-search"] as a fallback.
 *   - Debounced (250ms) predictive product search via Shopify's
 *     /search/suggest.json endpoint. In-flight requests are aborted so a
 *     stale response can never overwrite a newer query.
 *   - Render top product results (thumb / title / meta / price) into the
 *     partial's products group list.
 *   - Keyboard ↑ / ↓ navigation across result items + Esc to close.
 *
 * Progressive enhancement:
 *   The partial's <form> posts natively to {{ routes.search_url }} with
 *   name="q", so Enter (and a JS-disabled browser) always reaches the normal
 *   search results page. This script only layers predictive suggestions and
 *   overlay chrome on top — it never blocks the native submit.
 *
 * Public API (window.KoshoSearch):
 *   mount(rootEl?)      — wire up listeners (auto on DOMContentLoaded)
 *   open(initialQuery?) — show overlay, focus input, optional pre-fill
 *   close()
 *   toggle()
 *
 * DOM contract — see snippets/search-overlay.liquid.
 * ------------------------------------------------------------------------- */

(function (window, document) {
  'use strict';

  // ────────────────────────────────────────────────────────────────────────
  //  Constants
  // ────────────────────────────────────────────────────────────────────────
  var OPEN_CLASS    = 'search-overlay--open';
  var BODY_LOCK     = 'search-overlay-open';
  var ANIM_MS       = 250;
  var DEBOUNCE_MS   = 250;
  var MIN_QUERY_LEN = 2;
  var MAX_PRODUCTS  = 8;

  var prefersReducedMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ────────────────────────────────────────────────────────────────────────
  //  State
  // ────────────────────────────────────────────────────────────────────────
  var rootEl             = null;
  var panelEl            = null;
  var inputEl            = null;
  var clearBtn           = null;
  var closeBtn           = null;
  var backdropEl         = null;
  var resultsEl          = null;
  var initialEl          = null;
  var emptyEl            = null;
  var emptyQueryEl       = null;
  var predictiveEl       = null;
  var seeAllEl           = null;
  var productsSectionEl  = null;
  var productsListEl     = null;

  var isOpen            = false;
  var hideTimer         = null;
  var debounceTimer     = null;
  var lastTriggerEl     = null;
  var trap              = null;
  var currentController = null;
  var lastQuery         = '';

  // ────────────────────────────────────────────────────────────────────────
  //  Helpers
  // ────────────────────────────────────────────────────────────────────────
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function normalise(s) { return String(s == null ? '' : s).toLowerCase().trim(); }

  // Locale-aware root (handles markets prefixes like /en-sg/). Falls back to '/'.
  function shopRoot() {
    var root = '/';
    try {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        root = window.Shopify.routes.root;
      }
    } catch (e) {}
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root;
  }

  function suggestUrl(q) {
    return shopRoot() + 'search/suggest.json' +
      '?q=' + encodeURIComponent(q) +
      '&resources[type]=product' +
      '&resources[limit]=' + MAX_PRODUCTS +
      '&resources[options][unavailable_products]=last';
  }

  function searchPageUrl(q) {
    return shopRoot() + 'search?q=' + encodeURIComponent(q);
  }

  function activeCurrency() {
    try {
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
        return window.Shopify.currency.active;
      }
    } catch (e) {}
    return '';
  }

  function productImage(p) {
    if (!p) return '';
    if (typeof p.image === 'string' && p.image) return p.image;
    if (p.featured_image && typeof p.featured_image.url === 'string') return p.featured_image.url;
    return '';
  }

  // Predictive Search JSON returns price_min as a string. Some stores expose it
  // already formatted ("29.00"), others as bare cents ("2900"). Handle both;
  // values carrying a symbol/letters are shown verbatim. Verify against a live
  // store during `theme dev`.
  function formatPrice(p) {
    if (!p) return '';
    var raw = (p.price != null && p.price !== '') ? p.price : p.price_min;
    if (raw == null || raw === '') return '';
    var str = String(raw).trim();
    if (!str) return '';

    if (/[^\d.,\s]/.test(str)) return str;          // already has a currency marker

    var value = str;
    if (/^\d+$/.test(str)) {                         // bare integer → treat as cents
      value = (parseInt(str, 10) / 100).toFixed(2);
    }
    var cur = activeCurrency();
    return cur ? (cur + ' ' + value) : value;
  }

  // ────────────────────────────────────────────────────────────────────────
  //  Data — Shopify Predictive Search
  // ────────────────────────────────────────────────────────────────────────
  function fetchSuggest(q) {
    // Abort any in-flight request so a slower, older response can't win.
    if (currentController) { try { currentController.abort(); } catch (e) {} }
    var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    currentController = controller;

    var opts = { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' };
    if (controller) opts.signal = controller.signal;

    return fetch(suggestUrl(q), opts)
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        var results  = data && data.resources && data.resources.results;
        var products = results && results.products;
        return Array.isArray(products) ? products : [];
      });
  }

  // ────────────────────────────────────────────────────────────────────────
  //  Rendering
  // ────────────────────────────────────────────────────────────────────────
  function buildProductItem(p) {
    var li = document.createElement('li');
    li.className = 'search-overlay__item';

    var href  = p.url || '#';
    var name  = p.title || '';
    var meta  = [p.vendor, p.type || p.product_type].filter(Boolean).join(' · ');
    var price = formatPrice(p);
    var img   = productImage(p);

    li.innerHTML =
      '<a class="search-overlay__item-link" href="' + escapeHtml(href) + '">' +
        (img
          ? '<span class="search-overlay__item-media">' +
              '<img class="search-overlay__item-img" src="' + escapeHtml(img) + '" ' +
                   'alt="" loading="lazy" width="48" height="48">' +
            '</span>'
          : '') +
        '<span class="search-overlay__item-body">' +
          '<p class="search-overlay__item-title">' + escapeHtml(name) + '</p>' +
          (meta ? '<p class="search-overlay__item-meta">' + escapeHtml(meta) + '</p>' : '') +
        '</span>' +
        (price ? '<span class="search-overlay__item-price">' + escapeHtml(price) + '</span>' : '') +
      '</a>';

    return li;
  }

  function renderInitial() {
    if (initialEl)    initialEl.removeAttribute('hidden');
    if (emptyEl)      emptyEl.setAttribute('hidden', '');
    if (predictiveEl) predictiveEl.setAttribute('hidden', '');
  }

  // Quietly drop any visible results without surfacing an error state.
  function clearResults() {
    if (predictiveEl) predictiveEl.setAttribute('hidden', '');
    if (emptyEl)      emptyEl.setAttribute('hidden', '');
  }

  function renderProducts(query, products) {
    if (!resultsEl) return;
    if (initialEl) initialEl.setAttribute('hidden', '');

    if (!products || products.length === 0) {
      if (predictiveEl) predictiveEl.setAttribute('hidden', '');
      if (emptyEl) {
        emptyEl.removeAttribute('hidden');
        if (emptyQueryEl) emptyQueryEl.textContent = query;
      }
      return;
    }

    if (emptyEl) emptyEl.setAttribute('hidden', '');

    if (productsListEl && productsSectionEl) {
      productsListEl.innerHTML = '';
      for (var i = 0; i < products.length; i++) {
        productsListEl.appendChild(buildProductItem(products[i]));
      }
      productsSectionEl.removeAttribute('hidden');
    }

    if (seeAllEl) seeAllEl.setAttribute('href', searchPageUrl(query));

    if (predictiveEl) predictiveEl.removeAttribute('hidden');
  }

  // ────────────────────────────────────────────────────────────────────────
  //  Execute (debounced) — drives DOM updates from input changes
  // ────────────────────────────────────────────────────────────────────────
  function executeSearch(query) {
    var q = normalise(query);
    lastQuery = q;

    if (q.length < MIN_QUERY_LEN) {
      renderInitial();
      return;
    }

    fetchSuggest(q)
      .then(function (products) {
        // Ignore if the user has typed something newer while we were waiting.
        if (normalise(inputEl ? inputEl.value : q) !== q) return;
        renderProducts(q, products);
      })
      .catch(function (err) {
        // AbortError = superseded by a newer query; ignore.
        if (err && err.name === 'AbortError') return;
        // Any other failure: clear results quietly (no console noise).
        clearResults();
      });
  }

  function scheduleSearch(query) {
    if (debounceTimer) { window.clearTimeout(debounceTimer); debounceTimer = null; }
    debounceTimer = window.setTimeout(function () {
      debounceTimer = null;
      executeSearch(query);
    }, DEBOUNCE_MS);
  }

  // ────────────────────────────────────────────────────────────────────────
  //  Open / close
  // ────────────────────────────────────────────────────────────────────────
  function open(initialQuery) {
    if (!rootEl || isOpen) {
      if (rootEl && inputEl && typeof initialQuery === 'string') {
        inputEl.value = initialQuery;
        toggleClearBtn();
        scheduleSearch(initialQuery);
      }
      return;
    }
    isOpen = true;

    lastTriggerEl = document.activeElement;
    if (hideTimer) { window.clearTimeout(hideTimer); hideTimer = null; }

    rootEl.removeAttribute('hidden');
    rootEl.setAttribute('aria-hidden', 'false');
    // Force reflow so the fade transition picks up the class.
    void rootEl.offsetWidth;
    rootEl.classList.add(OPEN_CLASS);

    document.body.classList.add(BODY_LOCK);

    if (inputEl) {
      if (typeof initialQuery === 'string') inputEl.value = initialQuery;
      toggleClearBtn();
    }

    // Paint the correct initial render state.
    if (inputEl && normalise(inputEl.value).length >= MIN_QUERY_LEN) {
      executeSearch(inputEl.value);
    } else {
      renderInitial();
    }

    // Focus the input shortly after, so the fade is well underway.
    window.setTimeout(function () {
      if (inputEl) { try { inputEl.focus(); inputEl.select(); } catch (e) {} }

      if (window.KoshoFocusTrap && typeof window.KoshoFocusTrap.create === 'function') {
        try {
          trap = window.KoshoFocusTrap.create(panelEl || rootEl);
          trap.activate({ initialFocus: inputEl || undefined });
        } catch (e) { trap = null; }
      }
    }, prefersReducedMotion ? 0 : 60);

    document.dispatchEvent(new CustomEvent('koshosg:search:opened', {
      bubbles: true, detail: { query: (inputEl && inputEl.value) || '' }
    }));
  }

  function close() {
    if (!rootEl || !isOpen) return;
    isOpen = false;

    if (debounceTimer) { window.clearTimeout(debounceTimer); debounceTimer = null; }
    if (currentController) { try { currentController.abort(); } catch (e) {} currentController = null; }

    rootEl.classList.remove(OPEN_CLASS);
    rootEl.setAttribute('aria-hidden', 'true');
    document.body.classList.remove(BODY_LOCK);

    if (trap && typeof trap.deactivate === 'function') {
      try { trap.deactivate(); } catch (e) {}
    }
    trap = null;

    // Defer `hidden` until after the fade so the transition plays out.
    var delay = prefersReducedMotion ? 0 : ANIM_MS;
    hideTimer = window.setTimeout(function () {
      if (!isOpen) rootEl.setAttribute('hidden', '');
      hideTimer = null;
    }, delay);

    if (lastTriggerEl && typeof lastTriggerEl.focus === 'function') {
      try { lastTriggerEl.focus(); } catch (e) {}
    }
    lastTriggerEl = null;

    document.dispatchEvent(new CustomEvent('koshosg:search:closed', {
      bubbles: true, detail: { query: lastQuery }
    }));
  }

  function toggle() { isOpen ? close() : open(); }

  // ────────────────────────────────────────────────────────────────────────
  //  Event handlers
  // ────────────────────────────────────────────────────────────────────────
  function toggleClearBtn() {
    if (!clearBtn || !inputEl) return;
    if (inputEl.value && inputEl.value.length > 0) clearBtn.removeAttribute('hidden');
    else clearBtn.setAttribute('hidden', '');
  }

  function onInput() {
    toggleClearBtn();
    scheduleSearch(inputEl ? inputEl.value : '');
  }

  function onClearClick(ev) {
    ev.preventDefault();
    if (!inputEl) return;
    if (currentController) { try { currentController.abort(); } catch (e) {} currentController = null; }
    inputEl.value = '';
    toggleClearBtn();
    renderInitial();
    try { inputEl.focus(); } catch (e) {}
  }

  function onCloseClick(ev) { ev.preventDefault(); close(); }
  function onBackdropClick(ev) { ev.preventDefault(); close(); }

  function onSuggestionClick(ev) {
    var btn = ev.target.closest('[data-search-suggestion]');
    if (!btn) return;
    ev.preventDefault();
    var q = btn.getAttribute('data-search-suggestion') || btn.textContent || '';
    if (inputEl) {
      inputEl.value = q;
      toggleClearBtn();
      try { inputEl.focus(); } catch (e) {}
    }
    executeSearch(q);
  }

  function getResultItems() {
    if (!predictiveEl) return [];
    return $$('.search-overlay__item-link, .search-overlay__see-all', predictiveEl);
  }

  function onKeyDown(ev) {
    if (!isOpen) return;

    if (ev.key === 'Escape' || ev.key === 'Esc') {
      ev.preventDefault();
      close();
      return;
    }

    // Arrow navigation — only when the predictive panel is visible.
    if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
      var items = getResultItems();
      if (items.length === 0) return;

      var active = document.activeElement;
      var idx = items.indexOf(active);

      ev.preventDefault();
      if (ev.key === 'ArrowDown') {
        idx = idx < items.length - 1 ? idx + 1 : 0;
      } else {
        idx = idx > 0 ? idx - 1 : items.length - 1;
      }
      try { items[idx].focus(); } catch (e) {}
    }
  }

  function onHeaderOpenSearch(/* ev */) { open(); }

  function onDirectSearchClick(ev) {
    // Fallback when header.js isn't initialised yet. The trigger is a
    // <button type="button"> so preventDefault is a no-op, but keep it guarded.
    if (!window.KoshoHeader) ev.preventDefault();
    open();
  }

  // ────────────────────────────────────────────────────────────────────────
  //  Mount
  // ────────────────────────────────────────────────────────────────────────
  function mount(root) {
    rootEl = root || $('[data-search-overlay]');

    // Always bind the global header triggers, even if the partial isn't on the
    // page (so author tooling / late injection still works).
    document.addEventListener('kosho-header:open-search', onHeaderOpenSearch);
    var directBtns = $$('[data-action="open-search"]');
    for (var i = 0; i < directBtns.length; i++) {
      directBtns[i].addEventListener('click', onDirectSearchClick);
    }

    if (!rootEl) return false;

    panelEl           = $('.search-overlay__panel', rootEl);
    inputEl           = $('[data-search-input]', rootEl);
    clearBtn          = $('[data-search-clear]', rootEl);
    closeBtn          = $('[data-search-overlay-close]', rootEl);
    backdropEl        = $('[data-search-overlay-backdrop]', rootEl);
    resultsEl         = $('[data-search-results]', rootEl);
    initialEl         = $('[data-search-initial]', rootEl);
    emptyEl           = $('[data-search-empty]', rootEl);
    emptyQueryEl      = $('[data-search-empty-query]', rootEl);
    predictiveEl      = $('[data-search-predictive]', rootEl);
    seeAllEl          = $('[data-search-see-all]', rootEl);
    productsSectionEl = $('[data-search-group="products"]', rootEl);
    productsListEl    = productsSectionEl && $('[data-search-group-list]', productsSectionEl);

    // NOTE: the <form> submits natively to {{ routes.search_url }} (name="q"),
    // so Enter / no-JS reaches the normal search page. We deliberately do NOT
    // bind submit here.

    if (inputEl)    inputEl.addEventListener('input', onInput);
    if (clearBtn)   clearBtn.addEventListener('click', onClearClick);
    if (closeBtn)   closeBtn.addEventListener('click', onCloseClick);
    if (backdropEl) backdropEl.addEventListener('click', onBackdropClick);
    if (initialEl)  initialEl.addEventListener('click', onSuggestionClick);
    document.addEventListener('keydown', onKeyDown);

    return true;
  }

  // Auto-mount when DOM is ready.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { mount(); });
  } else {
    mount();
  }

  // ────────────────────────────────────────────────────────────────────────
  //  Public API
  // ────────────────────────────────────────────────────────────────────────
  window.KoshoSearch = {
    mount:  mount,
    open:   open,
    close:  close,
    toggle: toggle
  };

}(window, document));
